import './index.css';
import { createApp, markRaw, nextTick } from 'vue';
import { createPinia } from 'pinia';
import { WatchPiniaPlugin } from 'pinia-plugin-watch';
import VueTippy from 'vue-tippy';
import 'tippy.js/themes/light.css';
import 'tippy.js/dist/tippy.css';
import logger from '@/services/LoggerService.js';
import router from './router';
import moment from 'moment';
import App from './App.vue';
import i18n from './i18n';
import '@/helpers/globalMethods.js'; // Import the global methods file
import { useShowStore } from '@/stores/show';
import '@/services/OfflineMode';
import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/vue';
import { VERSION_NUMBER, BUILD_NUMBER } from './VersionInfo';
import { autoResizeSelect } from '@/helpers/commonFunctions.js';

const app = createApp(App);

let environment = import.meta.env.VITE_ENV_TYPE?.toUpperCase() ?? 'DEVELOPMENT';
if (['PRODUCTION', 'BETA', 'ALPHA'].includes(environment) == true) {
	console.log('Initializing Sentry');
	Sentry.init({
		app,
		dsn: 'https://b8527754ca00b12e4daebec51585619e@o427317.ingest.us.sentry.io/4509130143694848',
		release: `EVS-${environment}-${VERSION_NUMBER}-${BUILD_NUMBER}`,
		environment,
	});
}

app.config.globalProperties.ENV_TYPE = import.meta.env.VITE_ENV_TYPE?.toUpperCase() ?? 'DEVELOPMENT';
app.config.globalProperties.moment = moment;
app.config.globalProperties.Clarity = Clarity;

// Parse URL parameters on app start
logger.parseURLParams();
logger.initSentry(); // Initialize Sentry

// Intercept console methods to capture logs
logger.interceptConsoleMethods();
// Set logger globally
window.logger = logger;

app.use(i18n());

app.use(router);

app.use(VueTippy, {
	theme: 'light',
	directive: 'tippy', // => v-tippy
	component: 'tippy', // => <tippy/>
	componentSingleton: 'tippy-singleton', // => <tippy-singleton/>
	defaultProps: {
		allowHTML: true,
	},

	created() {
		this.can_copy = !!navigator.clipboard;
	},
	methods: {
		async copy(s) {
			await navigator.clipboard.writeText(s);
			alert('Copied!');
		},
	},
});

app.directive('click-outside', {
	beforeMount(el, binding) {
		el.clickOutsideEvent = function (event) {
			if (!(el === event.target || el.contains(event.target))) {
				binding.value(event, el);
			}
		};
		document.body.addEventListener('click', el.clickOutsideEvent);
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent);
	},
});

app.directive('click-outside-modal', {
	mounted(el, binding) {
		const { value } = binding;
		const modal_div = value.modal_backdrop_id ? document.getElementById(value.modal_backdrop_id) : null;
		const content_div = value.content_div_id ? document.getElementById(value.content_div_id) : null;

		const handleClickOutside = (event) => {
			if (!el.contains(event.target) && typeof value.action === 'function') {
				// Check if clicked outside the content div
				if (content_div && !content_div.contains(event.target) && modal_div.contains(event.target)) {
					value.action();
				}
			}
		};

		document.addEventListener('click', handleClickOutside);

		el.__clickOutsideHandler__ = handleClickOutside;
	},
	beforeUnmount(el) {
		// Remove event listener when the component is unmounted
		document.removeEventListener('click', el.__clickOutsideHandler__);
	},
});

app.directive('click-outside-event-table-modal', {
	mounted(el, binding) {
		const { value } = binding;
		const content_div = value.content_div_id ? document.getElementById(value.content_div_id) : null;

		const handleClickOutside = (event) => {
			if (content_div && !content_div.contains(event.target) && typeof value.action === 'function') {
				value.action();
			}
		};

		// Defer attach so the click that opened this modal doesn't immediately close it.
		// Use capture phase so @click.stop on other cells can't block it.
		setTimeout(() => {
			document.addEventListener('click', handleClickOutside, true);
		}, 0);

		el.__clickOutsideTableModalHandler__ = handleClickOutside;
	},
	beforeUnmount(el) {
		document.removeEventListener('click', el.__clickOutsideTableModalHandler__, true);
	},
});

app.directive('autofocus', {
	//Auto focus an element when it gets added to the dom
	beforeMount(el) {
		nextTick(() => {
			el.focus();
		});
	},
});
function checkForTruncation(element) {
	if (element.scrollWidth > element.clientWidth) {
		element.setAttribute('title', element?.textContent?.trim());
	} else {
		element.removeAttribute('title');
	}
}

app.directive('auto-truncate-title', {
	mounted(element) {
		checkForTruncation(element);
	},
	updated(element) {
		checkForTruncation(element);
	},
});

app.directive('auto-resize-select', autoResizeSelect);

const pinia = createPinia();
pinia.use(WatchPiniaPlugin);
pinia.use(({ store }) => {
	store.$router = markRaw(router);
});
app.use(pinia);

//  to make the store accessible globally
if (typeof window !== 'undefined') {
	window.stopAutoSaveShow = () => {
		const showStore = useShowStore();
		showStore.stopAutoSaveShow();
	};

	window.enableAutoSaveShow = () => {
		const showStore = useShowStore();
		showStore.enableAutoSaveShow();
	};
}

app.mount('#app');
